import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { coreFeatureService, UserNotification } from '../services/coreFeatureService';
import { blobticRealtimeService } from '../services/blobticRealtimeService';

// ============================================================================
// boubaker 23/08/2026 - Contexte NOTIFICATIONS PERSISTANTES uniquement
// (notifications serveur : cloche + drawer). Les toasts/alertes/confirmations
// passent par le point d'entrée UNIQUE blobticMessage (Commun/components/
// BlobticMessage.tsx) — plus aucun doublon de système de messages.
// ============================================================================

interface NotificationContextType {
  // Persistent User Notifications
  userNotifications: UserNotification[];
  unreadCount: number;
  fetchUserNotifications: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  deleteUserNotification: (id: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [userNotifications, setUserNotifications] = useState<UserNotification[]>([]);

  const fetchUserNotifications = useCallback(async () => {
    try {
      const notifs = await coreFeatureService.getUserNotifications();
      setUserNotifications(notifs);
    } catch (e) {
      console.error("Failed to fetch user notifications", e);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = blobticRealtimeService.subscribe((event) => {
      const obj = (event.objectName || '').toLowerCase();
      if (obj === 'user_notification' || obj === 'sys_notification' || obj === 'tabp_user_notification') {
        fetchUserNotifications();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [fetchUserNotifications]);

  const markAsRead = useCallback(async (id: number) => {
    const success = await coreFeatureService.markNotificationRead(id);
    if (success) {
      setUserNotifications(prev => prev.map(n => n.NOT_UIDF === id ? { ...n, NOT_IS_READ: true } : n));
    }
  }, []);

  const deleteUserNotification = useCallback(async (id: number) => {
    const success = await coreFeatureService.deleteNotification(id);
    if (success) {
      setUserNotifications(prev => prev.filter(n => n.NOT_UIDF !== id));
    }
  }, []);

  const unreadCount = userNotifications.filter(n => !n.NOT_IS_READ).length;

  const value: NotificationContextType = {
    userNotifications,
    unreadCount,
    fetchUserNotifications,
    markAsRead,
    deleteUserNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
